import { inject } from '@angular/core';
import {
  asyncScheduler,
  filter,
  from,
  map,
  Observable,
  observeOn,
  of,
  switchMap,
  take,
} from 'rxjs';

import { MultiGuard } from '@plugins/client/routing/multi-guard.creator';

import {
  BACKOFFICE_FALLBACK_LANGUAGE,
  BACKOFFICE_SUPPORTED_LANGUAGES,
} from '../constants/backoffice-translations';
import { BrowserLanguageService } from '../services/browser-language.service';
import { TranslateService } from '../services/translate.service';
import { Language } from '../services/types';

/**
 * Resolves the appropriate language for backoffice context.
 * If the browser language is not supported in backoffice {@link BACKOFFICE_SUPPORTED_LANGUAGES},
 * falls back to English.
 *
 * @param lang - The browser's language code
 * @returns The resolved language code for backoffice
 */
export const resolveBackofficeLanguage = (lang: Language): string => {
  if (
    BACKOFFICE_SUPPORTED_LANGUAGES.some(
      (supportedLang) => supportedLang === lang,
    )
  ) {
    return lang;
  }
  return BACKOFFICE_FALLBACK_LANGUAGE;
};

const verify = <T>(
  importStatement: () => Promise<T>,
  service: TranslateService,
) => {
  return from(importStatement()).pipe(
    observeOn(asyncScheduler),
    switchMap(() => {
      return service.languageLoaded$.pipe(
        filter((isReady) => isReady),
        take(1),
      );
    }),
  );
};

const verifyBackoffice = <T>(
  importStatement: () => Promise<T>,
  service: TranslateService,
  browserLanguageService: BrowserLanguageService,
): Observable<boolean> => {
  return verify(importStatement, service).pipe(
    switchMap(() => browserLanguageService.locale$),
    switchMap((browserLocale) => {
      // Extract language code from locale ("de-DE" -> "de")
      const browserLang = browserLocale.split('-')[0];
      const resolvedLang = resolveBackofficeLanguage(browserLang);

      if (resolvedLang === service.currentLanguage) {
        return of(true);
      }

      return service.use(resolvedLang).pipe(map(() => true));
    }),
  );
};

/** Standalone translation guard function. Supply an arrow function that
 * resolves to the target module/component which has the translation
 * loader decorator applied or imports a custom translation module. */
export const createTranslationGuard =
  <T>(importStatement: () => Promise<T>) =>
  () =>
    verify(importStatement, inject(TranslateService));

/**
 * Standalone backoffice translation guard function.
 * Ensures translations are loaded and the language is restricted to backoffice supported languages ({@link BACKOFFICE_SUPPORTED_LANGUAGES}).
 */
export const createBackofficeTranslationGuard =
  <T>(importStatement: () => Promise<T>) =>
  () =>
    verifyBackoffice(
      importStatement,
      inject(TranslateService),
      inject(BrowserLanguageService),
    );

/** For use with `queueGuards` helper. */
export const createTranslationGuardClass =
  <T>(importStatement: () => Promise<T>) =>
  () =>
    new TranslationGuardClass(importStatement);

export const createBackofficeTranslationGuardClass =
  <T>(importStatement: () => Promise<T>) =>
  () =>
    new BackofficeTranslationGuardClass(importStatement);

/** For use in `queueGuards` helper. Its usage is identical
 * to the `createTranslationGuard` function. */
class TranslationGuardClass extends MultiGuard {
  protected service = inject(TranslateService);
  constructor(protected importStatement: () => Promise<unknown>) {
    super();
  }

  protected canPass() {
    return verify(this.importStatement, this.service);
  }
}

/**
 * Backoffice translation guard class for use with `queueGuards` helper.
 * Ensures translations are loaded and the language is restricted to backoffice supported languages ({@link BACKOFFICE_SUPPORTED_LANGUAGES}).
 */
export class BackofficeTranslationGuardClass extends MultiGuard {
  protected service = inject(TranslateService);
  protected browserLanguageService = inject(BrowserLanguageService);
  constructor(protected importStatement: () => Promise<unknown>) {
    super();
  }

  protected canPass() {
    return verifyBackoffice(
      this.importStatement,
      this.service,
      this.browserLanguageService,
    );
  }
}
